import { Command } from "commander";
import chalk from "chalk";
import path from "path";
import { executeInstall, InstallReport } from "../install/installEngine.js";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { generateDiagnosticBundle } from "../diagnostics/bundleGenerator.js";

// Basic NPM package name validation regex (supporting scoped packages)
const PACKAGE_NAME_REGEX = /^(@[a-z0-9-~][a-z0-9-._~]*\/)?[a-z0-9-~][a-z0-9-._~]*$/i;

function getPackageVersionFromConfig(cwd: string, packageName: string): string | null {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) return null;
  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) return null;
  const dependencies = (pkg["dependencies"] as Record<string, string>) || {};
  const devDependencies = (pkg["devDependencies"] as Record<string, string>) || {};
  const version = dependencies[packageName] || devDependencies[packageName];
  return version ? version.replace(/^[\^~>=<]+/, "") : null;
}

export const installCommand = new Command("install")
  .description("Intelligently install a React Native library and synchronize configuration settings")
  .argument("<package>", "The npm library package name to install")
  .option("--dry-run", "Simulate the installation planning and native diagnostics checks")
  .option("--json", "Output installation report details in JSON format")
  .option("--verbose", "Show detailed install process command outputs")
  .option("--dev", "Save the package as a development dependency")
  .action(async (packageNameArg: string, options: { dryRun?: boolean; json?: boolean; verbose?: boolean; dev?: boolean }) => {
    try {
      const cwd = process.cwd();
      const packageName = packageNameArg.trim();

      // 1. Validate package name
      if (!PACKAGE_NAME_REGEX.test(packageName)) {
        if (options.json) {
          console.log(JSON.stringify({ success: false, error: "Invalid package name." }, null, 2));
        } else {
          console.log();
          console.log(chalk.red(`✖ Error: '${packageName}' is not a valid npm package name format.`));
          console.log();
        }
        process.exit(1);
        return;
      }

      // 2. Check if already installed
      const existingVersion = getPackageVersionFromConfig(cwd, packageName);
      if (existingVersion) {
        if (options.json) {
          console.log(JSON.stringify({ success: true, message: "Package is already installed.", version: existingVersion }, null, 2));
        } else {
          console.log();
          console.log(chalk.yellow(`⚠ Warning: '${packageName}' (v${existingVersion}) is already installed in package.json.`));
          console.log();
        }
        process.exit(0);
        return;
      }

      // 3. Handle Dry Run planning
      if (options.dryRun) {
        if (options.json) {
          console.log(JSON.stringify({
            planned: [
              { name: "Install package" },
              { name: "Synchronize native dependencies" },
              { name: "Validate project" }
            ]
          }, null, 2));
        } else {
          console.log();
          console.log(chalk.bold.underline("Planned Actions"));
          console.log();
          console.log(`  ${chalk.green("✓")} Install package`);
          console.log(`  ${chalk.green("✓")} Synchronize native dependencies`);
          console.log(`  ${chalk.green("✓")} Validate project`);
          console.log();
          console.log(chalk.yellow("No changes executed. (Dry run)"));
          console.log();
        }
        process.exit(0);
        return;
      }

      // 4. Run Execution
      const report = await executeInstall(cwd, packageName, !!options.dev, !!options.verbose, false, !!options.json);

      if (!report.success) {
        let reportPath = "";
        try {
          reportPath = await generateDiagnosticBundle("install", new Error(`Installation failed: ${report.installDetails}`));
        } catch {}

        if (options.json) {
          console.log(JSON.stringify({ 
            success: false, 
            details: report.installDetails,
            diagnosticBundle: reportPath
          }, null, 2));
        } else {
          console.log();
          console.log(chalk.red(`✖ Installation Failed: ${report.installDetails}`));
          if (reportPath) {
            console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
          }
          console.log();
        }
        process.exit(1);
        return;
      }

      const installedVersion = getPackageVersionFromConfig(cwd, packageName) || "unknown";

      // 5. Output formats
      if (options.json) {
        console.log(JSON.stringify({
          package: report.packageName,
          success: true,
          packageManager: report.packageManager,
          command: report.command,
          installedVersion,
          sync: report.syncResults.map((r) => ({ id: r.id, success: r.success, skipped: r.skipped })),
          setupGuidance: report.knowledge ? {
            ios: report.knowledge.iosRequirements,
            android: report.knowledge.androidRequirements,
          } : null,
          recommendations: report.recommendations,
        }, null, 2));
        process.exit(0);
        return;
      }

      // Text formatting
      console.log();
      console.log(chalk.bold.underline("Package Installed"));
      console.log(`  ${packageName} (v${installedVersion})`);
      console.log();

      console.log(chalk.bold("Synchronization"));
      console.log(`  ${chalk.green("✓")} Dependencies installed via ${report.packageManager}`);
      
      const podRes = report.syncResults.find((r) => r.id === "pods");
      if (podRes) {
        if (podRes.skipped) {
          console.log(`  ${chalk.dim("-")} Pods synchronization skipped`);
        } else if (podRes.success) {
          console.log(`  ${chalk.green("✓")} Pods synchronized successfully`);
        } else {
          console.log(`  ${chalk.red("✖")} Pods synchronization failed`);
        }
      }

      const gradleRes = report.syncResults.find((r) => r.id === "gradle-sync");
      if (gradleRes) {
        if (gradleRes.skipped) {
          console.log(`  ${chalk.dim("-")} Android validation skipped`);
        } else if (gradleRes.success) {
          console.log(`  ${chalk.green("✓")} Android validated successfully`);
        } else {
          console.log(`  ${chalk.red("✖")} Android validation failed`);
        }
      }
      console.log();

      if (report.knowledge) {
        console.log(chalk.bold.yellow("Additional Setup Required"));
        if (report.knowledge.iosRequirements.length > 0) {
          console.log(chalk.bold("iOS"));
          report.knowledge.iosRequirements.forEach((req) => console.log(`  * ${req}`));
        }
        if (report.knowledge.androidRequirements.length > 0) {
          console.log(chalk.bold("Android"));
          report.knowledge.androidRequirements.forEach((req) => console.log(`  * ${req}`));
        }
        console.log();

        if (report.knowledge.documentationUrl) {
          console.log(chalk.bold("Documentation"));
          console.log(`  ${chalk.blue.underline(report.knowledge.documentationUrl)}`);
          console.log();
        }
      }

      if (report.recommendations.length > 0) {
        console.log(chalk.bold.cyan("Recommended Next Steps"));
        report.recommendations.forEach((rec, idx) => {
          console.log(`  ${idx + 1}. ${rec}`);
        });
        console.log();
      }

      process.exit(0);
    } catch (err: any) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      let reportPath = "";
      try {
        reportPath = await generateDiagnosticBundle("install", errorObj);
      } catch {}

      if (options.json) {
        console.log(JSON.stringify({ 
          error: errorObj.message,
          diagnosticBundle: reportPath 
        }, null, 2));
      } else {
        console.log();
        console.log(chalk.red(`✖ Installation failed: ${errorObj.message}`));
        if (reportPath) {
          console.log(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
        }
        console.log();
      }
      process.exit(1);
    }
  });
